import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, NavigationEnd, NavigationStart, Router} from "@angular/router";
import {MatDialog} from "@angular/material/dialog";
import {
  DraftConfirmationDialogComponent
} from "../dialogs/draft-confirmation-dialog/draft-confirmation-dialog.component";
import {DownloadDialogComponent} from "../dialogs/lab-manager/download-dialog/download-dialog.component";
import {LabManagerSharedDataService} from "./lab-manager-shared-data.service";
import {LabManagerService} from "./lab-manager.service";
import {BasicLabManagerItemModel} from "../models/lab-manager/basic-lab-manager-item";
import {LabSubmissionDetailsModel} from "../models/lab-manager/lab-submission-details";
import {NavBarComponent} from "../nav-bar/nav-bar.component";
import {AgronimistModel} from "../models/lab-manager/agronimist";
import {CustomerModel} from "../order-to-cash/models-otc/customer-model";
import {LabCostItemModel} from "../models/lab-manager/lab-cost-item";
import { HttpResponse } from "@angular/common/http";
import {MatSnackBar} from "@angular/material/snack-bar";
import {UploadFileComponent} from "../dialogs/upload-file/upload-file.component";
import {User} from "../models/user";

@Component({
    selector: 'app-lab-manager',
    templateUrl: './lab-manager.component.html',
    styleUrls: ['./lab-manager.component.css'],
    standalone: false
})
export class LabManagerComponent implements OnInit, OnDestroy{

  labSubmissionDetails: LabSubmissionDetailsModel = null;
  submissionId: number;
  currentUser: User = JSON.parse(sessionStorage.getItem('user'));
  hasSavedClient: boolean = false;
  updatingChanges: boolean = true;
  labSubmissionDetailsValid: boolean = true;
  labHasSamples: boolean = false;
  labList: BasicLabManagerItemModel[];
  agronomistList: AgronimistModel[];
  isApprover: boolean = false;
  resultsAreCorrect: boolean = false;
  customerList: CustomerModel[];
  isLoadingCustomers: boolean = true;
  selectedCustomer: CustomerModel;
  analysisCostItems: LabCostItemModel[] = [];
  saveCount: number = 0;
  submissionTypes: BasicLabManagerItemModel[] = [];
  hasOneAnalysis: boolean = false;
  hasAllAnalysis: boolean = false;
  hasOneSample: boolean = false;
  statusList: BasicLabManagerItemModel[] = []

  constructor(
      public router: Router,
      private route: ActivatedRoute,
      private dialog: MatDialog,
      private labManagerSharedDataService: LabManagerSharedDataService,
      private labManagerService: LabManagerService,
      private nav: NavBarComponent,
      public snackBar: MatSnackBar,
  ) {
    router.events.subscribe((event) => {
      if(event instanceof NavigationEnd) {
        if(!event?.url.includes('/lab-manager/submissions') && !this.submissionId) {
          setTimeout(() => {
            this.submissionId = this.route.firstChild.snapshot.params['id'];
            this.getLabSubmissionDetails();
          }, 100)
        }

        if(event?.url.includes('/lab-manager/submissions')) {
          this.submissionId = null;
          this.hasSavedClient = false;
          this.labSubmissionDetails = null;
          this.analysisCostItems = [];
          this.labManagerSharedDataService.updateLabAnalysisCostItems([]);
        }
      }
    });

    this.labManagerSharedDataService.labSubmissionDetailsData$.subscribe(data => {
      if(data) {
        for(let item in data) {
          if(item) {
            this.labSubmissionDetails[item] = data[item]
          }
        }
      }
    });
  }

  ngOnInit(): void {
    this.getSubmissionTypes();
    this.getLabSubmissionStatusList();
  }

  getLabSubmissionStatusList() {
    this.labManagerService.getLabManagerStatusList().subscribe({
      next: (res: BasicLabManagerItemModel[]) => {
        this.statusList = res;
      }
    });
  }

  getSubmissionTypes() {
    this.labManagerService.getSubmissionTypes().subscribe((res: BasicLabManagerItemModel[]) => {
      this.submissionTypes = res;
    });
  }

  openBackDialog() {
    const dialogRef = this.dialog.open(DraftConfirmationDialogComponent, {
      data: {
        dialogType: 'back',
        dialogTitle: 'GO BACK?',
        dialogSubTitle: 'Unsaved changes will be lost, are you sure?'
      }
    });

    dialogRef.afterClosed().subscribe((res) => {
      if(res) {
        this.submissionId = null;
        this.hasSavedClient = false;
        sessionStorage.removeItem('currentAgent');
        this.labSubmissionDetails = null;
        this.analysisCostItems = [];
        this.labManagerSharedDataService.updateLabAnalysisCostItems([]);
        this.router.navigateByUrl('lab-manager');
      }
    })
  }

  getLabSubmissionDetails() {
    this.getLabList();
    this.getCustomerDetails();
    this.getAgronomistList();
    this.checkLabSubmissionSampleValidity();
    this.nav.toggleLoading(true);
    this.labManagerService.getLabSubmissionDetails(this.submissionId).subscribe({
      next: (res: LabSubmissionDetailsModel) => {
        this.labSubmissionDetails = res;
        this.isApprover = this.labSubmissionDetails?.agronomistUserId === this.currentUser.id;
        this.labManagerSharedDataService.updateApprover(this.isApprover);
        this.setSelectedCustomer();
        this.checkSubmissionHasOneAnalysis();
        this.retrieveLabAnalysisItems(false, this.labSubmissionDetails);

        this.hasSavedClient = !!this.labSubmissionDetails?.otcCustomerDetailId;

        this.labManagerSharedDataService.updateLabSubmissionDetails(this.labSubmissionDetails);
        this.updatingChanges = false;
        this.nav.toggleLoading(false);
      },
      error: () => {
        this.nav.toggleLoading(false);
      }})
  }

  getLabList() {
    this.labManagerService.getLabList().subscribe({
      next: (res) => {
        this.labList = res;
      }
    });
  }

  checkSubmissionHasOneAnalysis() {
    if(this.labSubmissionDetails?.id) {
      this.labManagerService.checkLabSubmissionAnalysisValidity(this.labSubmissionDetails.id).subscribe({
        next: (res: any) => {
          this.hasOneAnalysis = res?.hasOneAnalysis;
          this.hasAllAnalysis = res?.hasAllAnalysis;
        }
      })
    }
  }

  // checkSubmissionHasOneAnalysis() {
  //   this.labManagerService.checkSubmissionHasOneAnalysis(this.labSubmissionDetails.id).subscribe({
  //     next: (res: boolean) => {
  //       this.sampleHasAnalysis = res;
  //     }
  //   })
  // }

  checkLabSubmissionSampleValidity() {
    this.labManagerService.checkLabSubmissionSampleValidity(this.submissionId).subscribe({
      next: (res: boolean) => {
        this.hasOneSample = res;
      },
      error: () => {
        this.hasOneSample = false;
      }
    })
  }

  getCustomerDetails() {
    this.isLoadingCustomers = true;
    // Fast: get just the customer & payer for this submission instantly
    if (this.submissionId) {
      this.labManagerService.getSubmissionCustomers(this.submissionId).subscribe({
        next: (res) => {
          // Only use fast result if full list hasn't arrived yet
          if (this.isLoadingCustomers) {
            const initial = [];
            if (res.customer) initial.push(res.customer);
            if (res.payer && res.payer.id !== res.customer?.id) initial.push(res.payer);
            if (initial.length > 0) {
              this.customerList = initial;
              this.setSelectedCustomer();
            }
          }
        }
      });
    }
    // Background: load full customer list for dropdown changes
    this.labManagerService.getCustomers().subscribe({
      next: (res) => {
        this.customerList = res;
        this.isLoadingCustomers = false;
        this.setSelectedCustomer();
      }
    });
  }

  setSelectedCustomer() {
    if(this.customerList?.length > 0 && this.labSubmissionDetails?.otcCustomerDetailId) {
      this.selectedCustomer = this.customerList.find(c => c.id === this.labSubmissionDetails.otcCustomerDetailId);
    }
  }

  onLabSubmissionDetailChanges(event) {
    if(event?.labSubmissionDetails?.otcCustomerDetailId !== this.labSubmissionDetails?.otcCustomerDetailId) {
      this.hasSavedClient = false;
    } else if(this.labSubmissionDetails?.labManLabId !== event?.labSubmissionDetails?.labManLabId) {
      this.hasSavedClient = false;
    }
    this.labSubmissionDetails = event.labSubmissionDetails;
    this.labSubmissionDetailsValid = event.isValid;
  }

  getAgronomistList() {
    this.labManagerService.getAgronomistList().subscribe({
      next: (res) => {
        this.agronomistList = res;
      }
    })
  }

  openRejectDraftDialog() {
    const dialogRef = this.dialog.open(DraftConfirmationDialogComponent, {
      data: {
        dialogType: 'reject',
        dialogTitle: 'REJECT RESULTS',
        dialogSubTitle: 'Are you sure you want to reject this lab submission results?'
      }
    });

    dialogRef.afterClosed().subscribe(res => {
      if(res) {
        this.nav.toggleLoading(true);
        this.saveSubmission('REJECTED')
      }
    })
  }

  openDeleteDraftDialog() {
    const dialogRef = this.dialog.open(DraftConfirmationDialogComponent, {
      data: {
        dialogType: 'delete',
        dialogTitle: 'DELETE DRAFT',
        dialogSubTitle: 'Are you sure you want to delete this lab submission?'
      }
    });

    dialogRef.afterClosed().subscribe(res => {
      if(res) {
        this.nav.toggleLoading(true);
        this.labManagerService.deleteLabSubmission(this.submissionId).subscribe({
          next: () => {
            this.nav.toggleLoading(false);
            this.submissionId = null;
            this.hasSavedClient = false;
            this.labSubmissionDetails = null;
            this.analysisCostItems = [];
            this.labManagerSharedDataService.updateLabAnalysisCostItems([]);
            this.router.navigateByUrl('lab-manager');
          }
        })
      }
    })
  }

  saveLabAnalysisCostItems(tempSubmission: LabSubmissionDetailsModel) {
    if(this.analysisCostItems?.length > 0) {
      for(let costItem of this.analysisCostItems) {
        this.labManagerService.saveAnalysisCostItem(costItem).subscribe({
          next: () => {
            this.saveCount++;
            if(this.saveCount === this.analysisCostItems.length) {
              this.nav.toggleLoading(false);
              this.saveCount = 0;
              this.updateLabSubmissionDetails(tempSubmission);
            }
          },
          error: () => {
            this.saveCount++;
            if (this.saveCount === this.analysisCostItems.length) {
              this.nav.toggleLoading(false);
              this.saveCount = 0;
              this.updateLabSubmissionDetails(tempSubmission);
            }
          }
        });
      }
    } else {
      this.updateLabSubmissionDetails(tempSubmission);
    }
  }

  openApproveResultsDialog() {
    const dialogRef = this.dialog.open(DraftConfirmationDialogComponent, {
      data: {
        dialogType: 'approve',
        dialogTitle: 'APPROVE RESULTS',
        dialogSubTitle: 'Please insure that all information is correct before approving lab results.'
      }
    });

    dialogRef.afterClosed().subscribe(res => {
      if(res) {
        this.approveResults();
      }
    });
  }

  approveResults() {
    this.nav.toggleLoading(true);
    this.labManagerService.approveLabResults(this.labSubmissionDetails.id).subscribe({
      next: (res) => {
        this.labManagerService.getLabSubmissionDetails(this.submissionId).subscribe({
          next: (res: LabSubmissionDetailsModel) => {
            this.labSubmissionDetails = res;
            this.saveSubmission('RESULTS_APPROVED');
          }});
      },
      error: (error) => {
        this.nav.toggleLoading(false);
      }
    })
  }

  openSaveDraftDialog(submissionType: string) {
    let data = {};

    if(submissionType === 'SAVE') {
      data = {
        dialogType: 'save',
        dialogTitle: 'SAVE DRAFT',
        dialogSubTitle: 'Are you sure you want to save draft?'
      }
    } else {
      data = {
        dialogType: 'submit',
        dialogTitle: 'SUBMIT TO LAB',
        dialogSubTitle: 'Please ensure that all information is correct before submitting to lab.'
      }
    }
    const dialogRef = this.dialog.open(DraftConfirmationDialogComponent, {
      data: data
    });

    dialogRef.afterClosed().subscribe(res => {
      if(res) {
        this.saveSubmission(submissionType)
      }
    })
  }

  saveSubmission(submissionType: string) {
    let accountPayer = this.customerList.find(c => c.id === this.labSubmissionDetails.orderAccountId);
    let tempSubmission = this.labSubmissionDetails;
    if(submissionType === 'SAVE') {
      tempSubmission.labManStatusId = 1;
    } else if (submissionType === 'SUBMIT'){
      if(accountPayer?.sGrp === "ZAF" && (!this.labSubmissionDetails.coopNo || this.labSubmissionDetails?.coopNo?.trim().length === 0)) {
        tempSubmission.labManStatusId = 4;
      }  else {
        tempSubmission.labManStatusId = 2;
      }
    } else if(submissionType === 'RESULTS_UPLOADED') {
      tempSubmission.labManStatusId = 5;
    } else if(submissionType === 'AGRONOMIST') {
      tempSubmission.labManStatusId = 7;
    } else if(submissionType === 'REJECTED') {
      tempSubmission.labManStatusId = 8;
    } else {
      tempSubmission.labManStatusId = 6;
    }
    this.isApprover = this.labSubmissionDetails?.agronomistUserId === this.currentUser.id;
    this.labManagerSharedDataService.updateApprover(this.isApprover);

    if(tempSubmission.labManStatusId < 5) {
      if(this.analysisCostItems.length > 0 && this.router.url.includes('cost-calculator')) {
        this.saveLabAnalysisCostItems(tempSubmission);
      } else {
        this.retrieveLabAnalysisItems(true, tempSubmission);
      }
    } else {
      this.updateLabSubmissionDetails(tempSubmission);
    }

  }

  retrieveLabAnalysisItems(shouldSave: boolean, tempSubmission: LabSubmissionDetailsModel) {
    if(this.labSubmissionDetails?.labManLabId && this.labSubmissionDetails?.labManTypesId) {
      this.labManagerService.getAnalysisItems(this.labSubmissionDetails.labManLabId, this.labSubmissionDetails.labManTypesId).subscribe({
        next: (labManItems: BasicLabManagerItemModel[]) => {
          this.labManagerService.getLabSubmissionCalculator(this.labSubmissionDetails.id).subscribe({
            next: (res) => {
              this.analysisCostItems = [];

              for(let item of labManItems) {
                let index = res.findIndex(r => r.labManItemsId === item.id);

                if(index !== -1) {
                  this.analysisCostItems.push({...res[index],
                    description: item.description
                  });
                }
              }
              this.labManagerSharedDataService.updateLabAnalysisCostItems(this.analysisCostItems);
              if(shouldSave) {
                this.saveLabAnalysisCostItems(tempSubmission);
              }

              this.nav.toggleLoading(false);
            },
            error: () => {
              this.nav.toggleLoading(false);
            }
          })
        },
        error: () => {
          this.nav.toggleLoading(false);
        }
      });
    } else if(shouldSave) {
      this.updateLabSubmissionDetails(tempSubmission);
    }
  }

  updateLabSubmissionDetails(tempSubmission: LabSubmissionDetailsModel) {
    this.nav.toggleLoading(true);
    this.labManagerService.updateLabSubmissionDetail(tempSubmission, this.submissionId).subscribe({
      next: () => {
        this.labSubmissionDetails = tempSubmission;
        if(this.labSubmissionDetails.otcCustomerDetailId) {
          this.hasSavedClient = true;
        }
        this.nav.toggleLoading(false);
        this.labManagerSharedDataService.updateLabSubmissionDetails(this.labSubmissionDetails);
      },
      error: () => {
        this.nav.toggleLoading(false);
      }
    })
  }

  openUploadDialog() {
    console.log(this.labSubmissionDetails)
    const dialogRef = this.dialog.open(UploadFileComponent, {
      width: '420px'
    });

    dialogRef.afterClosed().subscribe(res => {
      if(res) {
        this.nav.toggleLoading(true);

        if(this.labSubmissionDetails.labManTypesId === 1) {
          this.labManagerService.uploadSoftTissueFile(this.labSubmissionDetails.id, this.labSubmissionDetails.labManLabId, this.labSubmissionDetails.labManTypesId, res, res?.type?.includes('text/csv')).subscribe({
            next: (res) => {
              this.snackBar.open("Successfully Uploaded File", 'close', {
                duration: 5000,
              });
              this.saveSubmission("RESULTS_UPLOADED");
            },
            error: (res) => {
              //i dont know why, but the successful message pops up here
              this.nav.toggleLoading(false);
              this.snackBar.open(res?.error?.text ?? res?.error, 'close', {
                duration: 5000,
              });
            }
          })
        } else {
          this.labManagerService.uploadSoilFile(this.labSubmissionDetails.id, res, res?.type?.includes('text/csv')).subscribe({
            next: (res) => {
              this.snackBar.open("Successfully Uploaded File", 'close', {
                duration: 5000,
              });
              this.saveSubmission("RESULTS_UPLOADED");
            },
            error: (res) => {
              //i dont know why, but the successful message pops up here
              this.nav.toggleLoading(false);
              this.snackBar.open(res?.error?.text ?? res?.error, 'close', {
                duration: 5000,
              });
            }
          })
        }
      }
    })
  }

  downloadLabReport() {
    this.nav.toggleLoading(true);
    this.labManagerService.downloadLabSubmissionExcel(this.submissionId, 2).subscribe({
      next: (resp: HttpResponse<Blob>) => {
        let data = resp.body


        if (data) {
          const url = window.URL.createObjectURL(data);
          const link = document.createElement('a');
          link.href = url;

          link.setAttribute('download', String(resp.headers.get('content-disposition')));

          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link);
        }
        this.snackBar.open("Download Successful", "close", {
          duration: 5000,
        });
        this.nav.toggleLoading(false);
      },
      error: () => {
        this.nav.toggleLoading(false);
        this.snackBar.open("Error Downloading Lab Report", "close", {
          duration: 5000,
        });
      }
    });
    // const dialogRef = this.dialog.open(DownloadDialogComponent, {
    //   width: '420px'
    // });

    // dialogRef.afterClosed().subscribe(res => {
    //   if(res) {
        
    //   }
    // })
  }

  ngOnDestroy(): void {
    this.submissionId = null;
    this.hasSavedClient = false;
    sessionStorage.removeItem('currentAgent');
    this.labSubmissionDetails = null;
    this.analysisCostItems = [];
    this.labManagerSharedDataService.updateLabAnalysisCostItems([]);
  }
}
